<template>
    <div class="flex flex-col gap-4">
        <div class="flex items-start gap-3">
            <div class="bg-accent-500/15 text-accent-300 flex size-10 shrink-0 items-center justify-center rounded">
                <fa :icon="['fas', 'lock']" class="text-lg" />
            </div>

            <div class="min-w-0 flex-1">
                <h3 class="text-primary-50 text-lg font-medium">{{ title }}</h3>
                <p class="text-primary-300 text-xs font-medium tracking-wide">
                    <t path="generic.paywall.eyebrow" />
                </p>
            </div>
        </div>

        <p v-if="description" class="text-primary-300 text-sm">{{ description }}</p>

        <!-- What unlocks it. Sunken so it reads as a quotation of the catalog rather
             than a second card competing with the heading. -->
        <!-- Media object: the meta line is a child of the title column, so it aligns
             under the name rather than under the icon. Everything below the name sits
             at one size (text-xs, the caption role) -- mixing text-sm and text-xs on a
             single row is what made these read as two unrelated fragments. -->
        <div class="bg-primary-800 ring-primary-400/20 flex items-start gap-2 rounded p-3 ring-1 ring-inset">
            <div class="bg-primary-500/30 text-primary-300 flex size-7 shrink-0 items-center justify-center rounded">
                <fa :icon="icon" class="text-xs" />
            </div>

            <div class="flex min-w-0 flex-1 flex-col gap-1">
                <div class="flex items-center gap-2">
                    <span class="text-primary-50 min-w-0 flex-1 truncate text-sm font-medium">{{ unlockedBy }}</span>
                    <badge v-if="addon?.trialable" variant="info">
                        <t path="admin.addons.trial" />
                    </badge>
                </div>

                <div class="text-primary-300 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                    <span v-if="addon && addon.price > 0" class="text-primary-50 font-medium">
                        <t :path="['admin.addons.price', { price: formatAddonPrice(addon.price) }]" />
                    </span>
                    <span v-else-if="tierName">
                        <t :path="['admin.addons.included_with', { tier: tierName }]" />
                    </span>
                    <span v-else-if="addon">
                        <t path="admin.addons.standalone" />
                    </span>

                    <!-- Binds the two facts into one line instead of leaving them adrift. -->
                    <span v-if="showFeatureCount" class="text-primary-400" aria-hidden="true">&middot;</span>
                    <span v-if="showFeatureCount">{{ featureLabel }}</span>

                    <span
                        v-for="game in games"
                        :key="game.slug"
                        :style="tagChipStyle(game.color)"
                        class="border-primary-400/20 text-primary-300 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium"
                    >
                        <fa :icon="iconForTagSlug(game.slug)" class="opacity-80" />
                        {{ game.name }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Reassurance, not decoration: without it an admin has no way to tell whether
             their customers are being shown adverts. They are not -- the API never sends
             non-admins the data this is built from. -->
        <p class="text-primary-400 flex items-start gap-2 text-xs">
            <fa :icon="['fas', 'eye-slash']" class="mt-0.5 shrink-0" />
            <span><t path="generic.paywall.admin_only" /></span>
        </p>

        <div class="flex flex-wrap items-center justify-end gap-2">
            <slot name="dismiss" />

            <v-button v-if="billingUrl" :href="billingUrl" target="_blank" rel="noopener" color="primary">
                <t :path="ctaPath" />
                <fa :icon="['fas', 'arrow-up-right-from-square']" class="ml-1.5 text-xs" />
            </v-button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatAddonPrice, iconForAddon, tierKey } from '~/components/addons/addonPresentation';
import { iconForTagSlug, tagChipStyle } from '~/components/admin/eggSpecificTagIcons';
import Badge from '~/components/ui/Badge.vue';
import { usePaywallStore } from '~/stores/paywall';
import { useSettingsStore } from '~/stores/settings';

/**
 * The single body shared by the paywall modal and the 402 page, so the two can never
 * describe the same lock differently.
 *
 * Degrades in two directions. A plan-only gate has no addon at all (`admin:opengraph`
 * is plan-gated with nothing to buy separately), and billing may simply be unreachable
 * -- in both cases the plan requirement still comes from the feature payload the app
 * already holds, so this renders a coherent offer rather than an empty card.
 */
const paywall = usePaywallStore();
const settings = useSettingsStore();
const { t: translate } = useI18n();

const target = computed(() => paywall.target);
const addon = computed(() => paywall.addon);

// The name of the thing they actually clicked, reusing the nav title that every route
// already has -- so no per-feature copy needs writing or translating.
const title = computed(() => {
    const routeName = target.value?.routeName;
    const key = routeName ? `navigation.${routeName}.title` : null;

    if (key && translate(key) !== key) return translate(key);

    return addon.value?.displayName ?? translate('generic.paywall.fallback_title');
});

const description = computed(() => addon.value?.description ?? undefined);

const tierName = computed(() => {
    const key = tierKey(addon.value?.plan ?? target.value?.requiresPlan);

    return key ? translate(key) : undefined;
});

// Named after the addon when there is one, otherwise the plan carries the offer. With
// neither we say nothing rather than invent a tier -- an addon sold outside every plan
// has no tier to name.
const unlockedBy = computed(() => {
    if (addon.value) return addon.value.displayName;
    if (tierName.value) return translate('generic.paywall.plan_unlock', { tier: tierName.value });

    return translate('generic.paywall.fallback_title');
});

const icon = computed((): [string, string] => (addon.value ? iconForAddon(addon.value.name) : ['fas', 'gem']));

const games = computed(() => addon.value?.tags.map((slug) => settings.eggSpecificTags.find((tag) => tag.slug === slug)).filter((tag) => !!tag) ?? []);

const featureLabel = computed(() =>
    translate('admin.addons.feature_count', { count: addon.value?.featureCount ?? 0 }, addon.value?.featureCount ?? 0),
);

// Drives both the count and its separator, so a lone middot can never be left hanging.
const showFeatureCount = computed(() => (addon.value?.featureCount ?? 0) > 0);

const requiresUpgrade = computed(() => addon.value?.requiresUpgrade ?? !!target.value?.requiresPlan);

// Name the tier when we know it -- "Upgrade to Enterprise" beats "Upgrade now". An
// addon sold outside every plan has no tier, so it falls back to the plain wording
// rather than rendering "Upgrade to ".
const ctaPath = computed<[string, Record<string, unknown>]>(() => {
    if (!requiresUpgrade.value) return ['generic.paywall.cta.get', {}];
    if (tierName.value) return ['generic.paywall.cta.upgrade', { tier: tierName.value }];

    return ['generic.paywall.cta.upgrade_now', {}];
});

// An addon carries its own deep link (already pointing at /plan when the tier is the
// blocker). A plan-only gate has no addon, and an owned addon's link is deliberately
// null, so fall back to the instance-level links from the settings payload rather than
// borrowing an unrelated addon's URL.
const billingUrl = computed(() => {
    if (addon.value?.billingUrl) return addon.value.billingUrl;

    const billing = settings.settings?.billing;

    return (requiresUpgrade.value ? billing?.plan_url : billing?.instance_url) ?? billing?.instance_url ?? undefined;
});
</script>
